import React, { useState } from 'react';
import type { IPayinObject, TPayinTransactionType } from 'api/resources/payin';
import { type IPayin } from 'api/resources/payin';
import { PaymentsApproved } from 'app/features/Payments/partials/PaymentsApproved';
import { PaymentsMailCheck } from 'app/features/Payments/partials/PaymentsMailCheck';
import { PaymentsModalTitle } from 'app/features/Payments/partials/PaymentsModalTitle';
import { PaymentsRainforest } from 'app/features/Payments/partials/PaymentsRainforest';
import { Modal } from '@design-manager-inc/ui';
import { usePostTransactions } from 'api/hooks/usePostTransactions';
import { useSearchParams } from 'react-router-dom';
import type { ETransactionType } from 'api/resources/transactions';
import { DEFAULT_PAYMENT_PROCESSOR } from 'api/resources/transactions';
import { useUpdateTransaction } from 'api/hooks/useUpdateTransaction';
import { type TPage } from '../Analytics/utils/constants';

export enum EPaymentsFormStatus {
	Loading,
	Idle,
	Submitting,
	Submitted,
	Failure,
	MailCheck,
}
interface IPaymentsModal {
	isOpen: boolean;
	onSuccess?: () => void;
	onClose: () => void;
	onStatusChange: (status: EPaymentsFormStatus) => void;
	payinInfo: IPayin;
	paymentsFormStatus: EPaymentsFormStatus;
	payinItems: IPayinObject[];
	page: TPage;
}

export const PaymentsModal = ({
	isOpen,
	onClose,
	onSuccess,
	onStatusChange,
	payinInfo,
	paymentsFormStatus,
	payinItems,
	page,
}: IPaymentsModal) => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';
	const [confirmationId, setConfirmationId] = useState('');
	const [showMailCheck, setShowMailCheck] = useState(false);
	const [totalPaid, setTotalPaid] = useState(0);
	const [checkTotal, setCheckTotal] = useState(0);
	const { mutateAsync: postTransactions } = usePostTransactions(token);
	const updateTransactions = useUpdateTransaction();

	const toggleMailCheck = () => setShowMailCheck((prev) => !prev);

	const onPayOnline = () => {
		onStatusChange(EPaymentsFormStatus.Idle);
		toggleMailCheck();
	};

	const onMailCheck = (amountToPay: number) => {
		setCheckTotal(amountToPay);
		onStatusChange(EPaymentsFormStatus.MailCheck);
	};

	const onSubmit = async (methodType: TPayinTransactionType) => {
		onStatusChange(EPaymentsFormStatus.Submitting);
		const paymentMethodUsed = payinInfo.payinOptions.find((opts) => {
			return opts.estimate.transactionType === methodType;
		});

		try {
			await postTransactions({
				processor: DEFAULT_PAYMENT_PROCESSOR,
				type: paymentMethodUsed?.estimate.transactionType as ETransactionType,
				batchToken: payinInfo.batchToken,
				processorTransactionId:
					paymentMethodUsed?.rainforestPayinConfigId as string,
				// Per the BE, we use `payinInfo.total` in `totalToPay` (not amountPaid)
				// https://designmanager.atlassian.net/browse/WEBPRO-5820?focusedCommentId=26695
				// However, we use `amountPaid` in `setTotalPaid` for the UI for the confirmation message
				totalToPay: payinInfo.total,
				untaggedObjects: payinItems,
			});
		} catch (error) {
			onStatusChange(EPaymentsFormStatus.Idle);
			throw error;
		}
	};

	const handleOnSuccess = async (
		confirmationId: string,
		methodType: TPayinTransactionType,
	) => {
		const paymentMethodUsed = payinInfo.payinOptions.find((opts) => {
			return opts.estimate.transactionType === methodType;
		});

		setConfirmationId(confirmationId);
		setTotalPaid(paymentMethodUsed?.estimate.total || 0);
		onStatusChange(EPaymentsFormStatus.Submitted);
		await updateTransactions({
			processor: DEFAULT_PAYMENT_PROCESSOR,
			type: paymentMethodUsed?.estimate.transactionType as ETransactionType,
			batchToken: payinInfo.batchToken,
			processorTransactionId:
				paymentMethodUsed?.rainforestPayinConfigId as string,
			// Per the BE, we use `payinInfo.total` in `totalToPay` (not amountPaid)
			// https://designmanager.atlassian.net/browse/WEBPRO-5820?focusedCommentId=26695
			// However, we use `amountPaid` in `setTotalPaid` for the UI for the confirmation message
			totalToPay: payinInfo.total,
			untaggedObjects: payinItems,
		});
		onSuccess?.();
	};

	const onInvalid = async () => {
		onStatusChange(EPaymentsFormStatus.Idle);
	};

	const onDeclined = async () => {
		onStatusChange(EPaymentsFormStatus.Idle);
	};

	return (
		<Modal
			isOpen={isOpen}
			overrides={{
				Title: PaymentsModalTitle,
			}}
			onClose={onClose}
			showCloseButton={true}
		>
			{(paymentsFormStatus === EPaymentsFormStatus.Idle ||
				paymentsFormStatus === EPaymentsFormStatus.Submitting) &&
				!showMailCheck && (
					<PaymentsRainforest
						showMailCheck={showMailCheck}
						toggleMailCheck={toggleMailCheck}
						payinInfo={payinInfo}
						onSubmit={onSubmit}
						onSuccess={handleOnSuccess}
						onInvalid={onInvalid}
						onDeclined={onDeclined}
						onMailCheck={onMailCheck}
						formStatus={paymentsFormStatus}
						page={page}
					/>
				)}

			{paymentsFormStatus === EPaymentsFormStatus.Submitted && (
				<PaymentsApproved
					totalPaid={totalPaid}
					confirmationId={confirmationId}
				/>
			)}

			{paymentsFormStatus === EPaymentsFormStatus.MailCheck &&
				showMailCheck && (
					<PaymentsMailCheck onPayOnline={onPayOnline} total={checkTotal} />
				)}
		</Modal>
	);
};
